import { useMemo, useSyncExternalStore } from 'react'

const subscribe = (onChange: () => void) => {
  window.addEventListener('popstate', onChange)

  return () => window.removeEventListener('popstate', onChange)
}

const getSnapshot = () => window.location.search

const getServerSnapshot = () => null

/**
 * Alternative to `useSearchParams` for statically rendered pages.
 *
 * `useSearchParams` makes Next.js skip server rendering up to the nearest Suspense boundary,
 * so crawlers without JavaScript only get the loading skeleton.
 * See https://nextjs.org/docs/app/api-reference/functions/use-search-params#prerendering
 *
 * This hook returns `null` on the server and during hydration, and the real search params
 * afterwards. It updates on browser back/forward (`popstate`), but NOT on `router.push`/`replace`
 * or `history.pushState`/`replaceState`, since those fire no event. Only use it to read the URL
 * once after hydration, keep state that changes afterwards in React state or a store.
 */
export const useClientSearchParams = () => {
  const search = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

  return useMemo(() => (search == null ? null : new URLSearchParams(search)), [search])
}
